import React, { useState } from 'react';
import { Flex } from 'antd';
import { faXmark } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { css } from 'styled-components';

import { trackEvent } from 'jf/analytics/Analytics';
import { RosterClient, SlackTargetingClient, SlackUser } from 'jf/api';
import { useDevExTheme } from 'jf/common/themes/DevExTheme';
import { DevExButton } from 'jf/components/DevExButton';
import { DevExTag } from 'jf/components/DevExTag';
import { DevExText } from 'jf/components/DevExText';
import { useClientMutation, useClientQuery } from 'jf/utils/useClientQuery';

const COLLAPSED_TARGET_COUNT = 10;

export const SlackTargetTagList: React.FC<{
  unsavedSlackTargets?: SlackUser[];
  onChange?: (slackTargets: SlackUser[]) => void;
  studyRef: string;
}> = (props) => {
  const { isLoading: isExpanding } = useClientMutation(SlackTargetingClient.expandSlackEntity);

  const theme = useDevExTheme();
  const { data: targetedSlackRespondents } = useClientQuery(RosterClient.getRosterTargets, {
    studyRef: props.studyRef,
    slackSelected: true,
  });
  const slackUsers = targetedSlackRespondents?.map((respondent) => respondent.slack!) || [];
  const [isOldCollapsed, setIsOldCollapsed] = useState(true);

  return (
    <Flex vertical gap={theme.variable.spacing.sm}>
      {!!slackUsers.length && (
        <DevExText type="secondary" fontSize="xs">
          {slackUsers.length} target respondent
          {slackUsers.length === 1 ? '' : 's'}
        </DevExText>
      )}
      <>
        <Flex gap={theme.variable.spacing.sm} wrap="wrap" align="center">
          {slackUsers
            .slice(0, isOldCollapsed ? COLLAPSED_TARGET_COUNT : undefined)
            .map((target) => (
              <DevExTag key={target.slackUserId} color="purple">
                <div>{target.userName}</div>
              </DevExTag>
            ))}
          {isOldCollapsed && slackUsers.length > COLLAPSED_TARGET_COUNT && (
            <DevExButton
              size="small"
              type="text"
              onClick={() => setIsOldCollapsed(false)}
              style={{ marginLeft: `-${theme.variable.spacing.sm}` }}
            >
              + {slackUsers.length - COLLAPSED_TARGET_COUNT} others
            </DevExButton>
          )}
        </Flex>

        {!!props.unsavedSlackTargets && !!props.unsavedSlackTargets.length && (
          <DevExText type="secondary" fontSize="xs">
            {props.unsavedSlackTargets.length} new target respondent
            {props.unsavedSlackTargets.length === 1 ? '' : 's'}
          </DevExText>
        )}

        <Flex gap={theme.variable.spacing.sm} wrap="wrap" align="center">
          {props.unsavedSlackTargets &&
            props.unsavedSlackTargets.map((target) => {
              return (
                <DevExTag
                  key={target.slackUserId}
                  color="purple"
                  css={css`
                    &&&& {
                      padding-block: 0;
                      padding-right: ${theme.variable.spacing.xs};
                    }
                  `}
                >
                  <Flex align="center">
                    {target.userName ? target.userName : target.realName}
                    <DevExButton
                      icon={<FontAwesomeIcon icon={faXmark} />}
                      size="small"
                      type="text"
                      onClick={() => {
                        const removedTargets = [...(props.unsavedSlackTargets || [])].filter(
                          ({ slackUserId }) => slackUserId !== target.slackUserId
                        );
                        if (props.onChange) {
                          props.onChange(removedTargets);
                        }
                        trackEvent(`survey-analyzer:target-user:remove`, {
                          id: target.slackUserId,
                        });
                      }}
                    />
                  </Flex>
                </DevExTag>
              );
            })}

          {isExpanding && <DevExTag>Adding targets...</DevExTag>}

          {props.unsavedSlackTargets && !!props.unsavedSlackTargets.length && !isExpanding && (
            <DevExButton
              size="small"
              type="text"
              onClick={() => {
                if (props.onChange) {
                  props.onChange([]);
                }
              }}
              style={{ marginLeft: `-${theme.variable.spacing.sm}` }}
            >
              Clear all
            </DevExButton>
          )}
        </Flex>
      </>
    </Flex>
  );
};
